import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IAuthAggregate } from '../auth.aggregate';
import { IRouter } from '../../../infrastructure/router';
import { RouteParams, Routes } from '../../../routes-table';
import { TWO_MINUTES } from '../../../constants/date-time.constants';
import { USER_PERMISSIONS } from '../../../constants/permission.boilerplate';
import { IUserDetailsDTO, IUsersDAO } from '../../../api/users.dao';
import { ACCESS_TOKEN, AUTH_ROUTE, CLIENT_ID, REFRESH_TOKEN, SAVED_ROUTE_STORAGE_NAME, USER_STATE } from '../../../constants/common.constants';
import TrackingServiceProvider from '../../../tracking-service/tracking-service-provider';
import pkceChallenge from 'pkce-challenge';
import { IRuntimeConfiguration } from '../../../infrastructure/runtime-configuration';
import { AuthServiceConfigurator } from './auth-service-configurator';


/**
 * implementation of IAuthAggregate, which retrieve auth token using fusionauth
 */
class FusionAuthAggregate extends IAuthAggregate {
    @mobx.observable
    private authenticated: boolean;

    @mobx.observable
    private permissionAvailable: boolean;

    private trackingServiceProvider: TrackingServiceProvider;
    private runtimeConfiguration: IRuntimeConfiguration;
    private authCallbackUri: string;

    constructor(
        @IoC.Inject private readonly routingService = IoC.Container.get<IRouter<RouteParams>>(IRouter),
        @IoC.Inject private readonly usersDAO = IoC.Container.get(IUsersDAO),
        @IoC.Inject private readonly serviceConfigurator: AuthServiceConfigurator = IoC.Container.get(AuthServiceConfigurator)

    ) {
        super();
        this.trackingServiceProvider = IoC.Container.get(TrackingServiceProvider);
        this.runtimeConfiguration = IoC.Container.get(IRuntimeConfiguration);
        const baseUrl = this.runtimeConfiguration.getBaseUrl();
        const baseUrlSuffix = baseUrl ? '/' + baseUrl : '';
        this.authCallbackUri = window.location.origin + baseUrlSuffix + Routes.AuthCodeFlowCallback;
    }

    public initialize = async (): Promise<void> => {
        // in case we're on the logout screen - force authentication (e.g. move user to fusion login screen)
        this.routingService.listen(Routes.LogoutScreen, () => {
            this.authenticate();
        });
        this.routingService.listen(Routes.SsoIdp, () => {
            this.navigateIdpUser();
        });
        this.routingService.listen(Routes.AuthCodeFlowCallback, () => {
            if (!localStorage.getItem(USER_STATE)) {
                const routeState = this.routingService.router.getState();
                if (routeState) {
                    const { params } = routeState;
                    if (params?.userState && params?.code) {
                        localStorage.setItem(USER_STATE, params.userState);
                        this.authenticatedUserFlow(params.code);
                    } else if (params?.error) {
                        return;
                    } else {
                        this.routingService.navigate(Routes.Default, {});
                    }
                }
            }
        });
    };

    private authenticatedUserFlow = async (code: string) => {
        const clientId = await this.getFusionAuthClientId();
        const result = await this.exchangeOAuthCodeForAccessToken(clientId, code);
        if (result?.ok) {
            result.json().then(response => {
                localStorage.setItem(ACCESS_TOKEN, response.access_token);
                localStorage.setItem(REFRESH_TOKEN, response.refresh_token);
                this.setAuthenticated(true);
                this.routingService.navigate(Routes.Default, {});
            }).then(() => {
                window.location.reload();
            });
        } else {
            this.routingService.navigate(Routes.Default, {});
            await this.forceLogout();
        }
    };

    public authenticate = async () => {
        let login_hint: string;
        const routeState = this.routingService.router.getState();
        if (routeState) {
            const { path, params } = routeState;
            login_hint = params?.idp;
            if (!login_hint && path.slice(0, 5) !== AUTH_ROUTE) {
                localStorage.setItem(SAVED_ROUTE_STORAGE_NAME, JSON.stringify({
                    path: path,
                    params: params
                }));
            }
        }
        if (!localStorage.getItem(USER_STATE))
            this.login(login_hint);
    };

    public isAuthenticated = () => {
        return this.authenticated;
    };

    public isPermissionAvailable = () => {
        return this.permissionAvailable;
    };

    @mobx.action
    private setAuthenticated(authenticated) {
        this.authenticated = authenticated;
    }

    @mobx.action
    private setPermissionAvailable(permissionAvailable) {
        this.permissionAvailable = permissionAvailable;
    }

    private login = async (login_hint: string) => {
        const clientId = await this.getFusionAuthClientId();
        if (clientId) {
            const { code_challenge, code_verifier } = pkceChallenge(43);
            localStorage.setItem(CLIENT_ID, clientId);
            localStorage.setItem('code_verifier', code_verifier);
            let loginUrl = `https://${this.runtimeConfiguration.getFusionAuthDomain()}.com/oauth2/authorize?client_id=${clientId}&redirect_uri=${this.authCallbackUri}&response_type=code&code_challenge=${code_challenge}&code_challenge_method=S256&scope=offline_access%20openid`;
            loginUrl = (login_hint) ? loginUrl.concat(`&login_hint=${login_hint}`) : loginUrl;
            window.location.href = loginUrl;
        }
    };

    public logout = async () => {
        const sessionToken = await this.getSessionToken();
        if (sessionToken) {
            await this.forceLogout();
        } else {
            this.authenticate();
        }
    };

    private forceLogout = async () => {
        const clientId = await this.getFusionAuthClientId();
        localStorage.clear();
        this.trackingServiceProvider.get().reset();
        window.location.href = `https://${this.runtimeConfiguration.getFusionAuthDomain()}.com/oauth2/logout?client_id=${clientId}`;
    };

    public getCurrentUser = async (): Promise<IUserDetailsDTO> => {
        return this.usersDAO.getCurrentUser();
    };

    public getUserPermissions = async () => {
        this.setPermissionAvailable(false);
          await this.getCurrentUser().then(({
                isSuperuser,
                roles
            }: IUserDetailsDTO) => {
                localStorage.setItem(USER_PERMISSIONS, JSON.stringify({
                    isSuperuser: isSuperuser,
                    permissions: Array.from(roles?.reduce((permissionsList, role) => {
                        role.permissions?.forEach(permission => permissionsList.add(permission));
                        return permissionsList;
                    }, new Set()))
                }));

                this.setPermissionAvailable(true);
            });
    };

    public getSessionToken = async () => {
        let token = localStorage.getItem(ACCESS_TOKEN);
        try {
            const timeStamp = new Date().getTime() / 1000;
            const decodedJwt = JSON.parse(atob(token.split('.')[1]));
            if (decodedJwt.exp - timeStamp < TWO_MINUTES) {
                try {
                    const refreshToken = localStorage.getItem(REFRESH_TOKEN);
                    const userCode = localStorage.getItem(USER_STATE);
                    const clientId = await this.getFusionAuthClientId();
                    const result = await this.exchangeRefreshTokenForAccessTokenAPI(refreshToken, clientId, userCode);
                    if (result?.ok) {
                        const response = await result.json();
                        localStorage.setItem(ACCESS_TOKEN, response.access_token);
                        localStorage.setItem(REFRESH_TOKEN, response.refresh_token);
                        token = response.access_token;
                    } else {
                        this.forceLogout();
                    }
                } catch (error) {
                    this.forceLogout();
                }
            }
            this.setAuthenticated(true);
            return token;
        } catch (error) {
            this.setAuthenticated(false);
            return null;
        }
    };

    private exchangeOAuthCodeForAccessToken = async (clientId: string, code: string) => {
        let myHeaders = new Headers();
        myHeaders.append('Content-Type', 'application/x-www-form-urlencoded');
        let body = new URLSearchParams();
        body.append('code', code);
        body.append('client_id', clientId);
        body.append('grant_type', 'authorization_code');
        body.append('redirect_uri', this.authCallbackUri);
        body.append('code_verifier', localStorage.getItem('code_verifier'));
        localStorage.removeItem('code_verifier');
        const requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: body
        };
        return fetch(`https://${this.runtimeConfiguration.getFusionAuthDomain()}.com/oauth2/token`, requestOptions);
    };

    private exchangeRefreshTokenForAccessTokenAPI = async (refreshToken: string, clientId: string, userCode: string) => {
        let myHeaders = new Headers();
        myHeaders.append('Content-Type', 'application/x-www-form-urlencoded');
        let body = new URLSearchParams();
        body.append('client_id', clientId);
        body.append('refresh_token', refreshToken);
        body.append('grant_type', 'refresh_token');
        body.append('user_code', userCode);
        body.append('scope', 'offline_access openid');
        const requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: body
        };
        return fetch(`https://${this.runtimeConfiguration.getFusionAuthDomain()}.com/oauth2/token`, requestOptions);
    };

    private retrieveUserUsingJWT = async (token: string) => {
        const requestOptions = {
            method: 'GET',
            headers: {
                'Authorization': 'Bearer ' + token
            }
        };
        return fetch(`https://${this.runtimeConfiguration.getFusionAuthDomain()}.com/oauth2/userinfo`, requestOptions);
    };

    private getFusionAuthClientId = async () => {
        let clientId = localStorage.getItem(CLIENT_ID);
        return (clientId) ? clientId : await this.serviceConfigurator.getFusionAuthClientId();
    };

    public exchangeRefreshTokenForAccessToken = async () => {
        try {
            const refreshToken = localStorage.getItem(REFRESH_TOKEN);
            const userCode = localStorage.getItem(USER_STATE);
            const clientId = await this.getFusionAuthClientId();
            const result = await this.exchangeRefreshTokenForAccessTokenAPI(refreshToken, clientId, userCode);
            if (result?.ok) {
                const response = await result.json();
                localStorage.setItem(ACCESS_TOKEN, response.access_token);
                localStorage.setItem(REFRESH_TOKEN, response.refresh_token);
                window.location.reload();
            } else {
                this.forceLogout();
            }
        } catch (error) {
            this.forceLogout();
        }
    };

    private navigateIdpUser = async () => {
        const sessionToken = await this.getSessionToken();
        if (sessionToken)
            this.routingService.navigate(Routes.Default, {});
        else if (localStorage.getItem(USER_STATE))
            await this.forceLogout();
    };
}

export { FusionAuthAggregate, USER_PERMISSIONS };
